import { useTranslation } from 'react-i18next';

import type { UserDto } from '@/services/ws-permissions/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useAuth } from '@/js/libs/use-auth';
import { useAbortOnUnmount } from '@/js/libs/use-request';

import { changeUiLanguage, getUiLanguageReplacement } from 'src/utils/uiLanguage.ts';

type SavedProfile = Pick<UserDto, 'firstName' | 'lastName' | 'preferredLanguages'>;
type ApplySavedProfile = (savedProfile: SavedProfile) => void;

// Puts the name and the languages saved to the profile of the identity into the loaded users, so the navigation and the
// language switcher show them at once: ws-permissions, which the users are loaded from, takes the saved profile over only
// after a while. The email stays as loaded, since an impersonated user is matched by it. The profile of an impersonated
// user is not the logged user's, which stays as it is. Without a selection the identity is the logged user, and both get
// the one updated user. Saved languages that leave out the UI language switch the UI to the first of them, unless they
// are an impersonated user's: the UI language stays the admin's, for this tab and the next page loads.
export const useApplySavedProfile = (): ApplySavedProfile => {
  const { i18n } = useTranslation();
  const { identity, impersonatedUser, loggedUser, setIdentity, setLoggedUser } = useAuth();
  // A switch of the identity puts the page behind the loader and a logout closes it, so a save that ends after the
  // unmount holds users that may no longer be loaded, and leaves them as they are.
  const unmountSignal = useAbortOnUnmount();

  return useCallbackRef<ApplySavedProfile>(({ firstName, lastName, preferredLanguages }) => {
    if (!identity || unmountSignal.aborted) {
      return;
    }

    const savedFields = { firstName, lastName, preferredLanguages };
    const updatedIdentity = { ...identity, ...savedFields };
    setIdentity(updatedIdentity);
    if (loggedUser && !impersonatedUser) {
      setLoggedUser(identity === loggedUser ? updatedIdentity : { ...loggedUser, ...savedFields });
    }

    const uiLanguageReplacement = impersonatedUser ? undefined : getUiLanguageReplacement(i18n.language, preferredLanguages ?? []);
    if (uiLanguageReplacement) {
      void changeUiLanguage(i18n, uiLanguageReplacement);
    }
  });
};
